import styled, {css} from 'styled-components';
import Colours from '../../constants/colours';
import Typography from '../../constants/typography';
import {Button, ButtonVariant} from '../Button';
import {NAVIGATION_COLLAPSE_POINT} from '../../constants/layouts';

interface BackgroundProps {
  shadow: boolean;
}

export const Background = styled.div<BackgroundProps>`
  ${({shadow}) => css`
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 600px;
    background-color: #222;
    overflow: hidden;
    ${shadow &&
    css`
      box-shadow: 1px 1px 5px 2px rgba(0, 0, 0, 0.2),
        1px 1px 20px 10px rgba(0, 0, 0, 0.4);
    `}
  `}
`;

export const BackgroundImage = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: scale(4);
  z-index: 0;
  filter: blur(2rem) saturate(150%);

  /**
    remove the blur in firefox as it's currently super slow
    desaturating isn't as nice but later we'll replace this
    with something done with WebGL
   */
  @-moz-document url-prefix() {
    filter: saturate(30%);
  }
`;

interface TitleProps {
  foregroundColour: string;
}

export const Title = styled.h2<TitleProps>`
  ${({foregroundColour}) => css`
    font-size: ${Typography.sectionHeader.desktop.fontSize};
    line-height: ${Typography.sectionHeader.mobile.lineHeight};
    color: ${foregroundColour};
    transition: color 0.3s;

    @media (max-width: ${NAVIGATION_COLLAPSE_POINT}) {
      font-size: ${Typography.sectionHeader.mobile.fontSize};
      line-height: ${Typography.sectionHeader.mobile.lineHeight};
    }
  `}
`;

export const AudioPlayerCanvas = styled.canvas`
  display: none;
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  text-align: center;
  transform: translate(-50%, -50%);
  z-index: 50;
`;

export const Artwork = styled.div`
  position: relative;
  display: flex;
  box-shadow: 0 2px 10px #111;
  max-width: 220px;
`;

export const Name = styled.h2`
  color: ${Colours.white};
  font-size: 1.8rem;
  transition: color 0.3s;
`;

export const Duration = styled.div`
  color: ${Colours.white};
  font-size: 1.1rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 1px;
  margin-top: 0.5rem;
  transition: color 0.3s;
`;

export const ProgressOuter = styled.div`
  position: absolute;
  bottom: -1px;
  left: 0px;
  width: 100%;
  height: 7px;
  background-color: #111111cc;
  overflow: hidden;
`;

export const ProgressInner = styled.div`
  width: 100%;
  height: 7px;
  background-color: ${Colours.white};
`;

export const Image = styled.img`
  width: 100%;
  height: 100%;
`;

export const Controls = styled.div`
  display: flex;
  justify-content: center;
`;

export const ControlButton = styled(Button)`
  display: flex;
  justify-content: space-around;
  font-weight: bold;
  width: 50px;
  background-size: 14px;
  padding: 1.8rem;
  border-radius: 0px;

  &:hover {
    background-color: transparent;
  }
`;

interface ControlButtonProps {
  foregroundColour: string;
  variant: ButtonVariant;
}

export const PlayButton = styled(ControlButton)<ControlButtonProps>`
  ${({foregroundColour}) => css`
    background: ${
      foregroundColour === Colours.white
        ? "url('/images/icons/playback-play-white.svg')"
        : "url('/images/icons/playback-play.svg')"
    }
      no-repeat center;
    background-size: 14px;
    border: 1px solid ${foregroundColour};
    border-left: none;
    border-right: none;

    &:hover {
      border-top: 1px solid ${foregroundColour};
      border-bottom: 1px solid ${foregroundColour};
      border-left: none;
      border-right: none;
    }

    &:active {
      background-image: ${
        // needs to be the inverse on active
        foregroundColour === Colours.white
          ? "url('/images/icons/playback-play.svg'"
          : "url('/images/icons/playback-play-white.svg'"
      } );
      border-left: none;
      border-right: none;
      background-color: ${foregroundColour};
      border-color: ${foregroundColour};
    }
  `}
`;

export const StopButton = styled(ControlButton)<ControlButtonProps>`
  ${({foregroundColour}) => css`
    background: ${
      foregroundColour === Colours.white
        ? "url('/images/icons/playback-stop-white.svg')"
        : "url('/images/icons/playback-stop.svg')"
    }
      no-repeat center;
    background-size: 16px;
    border: 1px solid ${foregroundColour};
    border-left: none;
    border-right: none;

    &:hover {
      border-top: 1px solid ${foregroundColour};
      border-bottom: 1px solid ${foregroundColour};
      border-left: none;
      border-right: none;
    }

    &:active {
    background-image: ${
      // inverse on active
      foregroundColour === Colours.white
        ? "url('/images/icons/playback-stop.svg'"
        : "url('/images/icons/playback-stop-white.svg'"
    });
      border-left: none;
      border-right: none;
      background-color: ${foregroundColour};
      border-color: ${foregroundColour};
    }
  `}
`;

export const NextButton = styled(ControlButton)<ControlButtonProps>`
  ${({foregroundColour}) => css`
    background: ${foregroundColour === Colours.white
        ? "url('/images/icons/playback-next-white.svg')"
        : "url('/images/icons/playback-next.svg')"}
      no-repeat center;
    background-size: 16px;
    border: 1px solid ${foregroundColour};

    &:active {
      background: ${foregroundColour}
        ${
          // inverse on active
          foregroundColour === Colours.white
            ? "url('/images/icons/playback-next.svg')"
            : "url('/images/icons/playback-next-white.svg')"
        }
        no-repeat center;
      background-size: 16px;
      background-color: ${foregroundColour};
      border-color: ${foregroundColour};
    }
  `}
`;

export const PrevButton = styled(ControlButton)<ControlButtonProps>`
  ${({foregroundColour}) => css`
  background: ${
    foregroundColour === Colours.white
      ? "url('/images/icons/playback-prev-white.svg')"
      : "url('/images/icons/playback-prev.svg')"
  } no-repeat center;
    background-size: 16px;
    border: 1px solid ${foregroundColour};

    @media(hover: hover){

    &:active {
    background: ${foregroundColour} ${
    // inverse on active
    foregroundColour === Colours.white
      ? "url('/images/icons/playback-prev.svg')"
      : "url('/images/icons/playback-prev-white.svg')"
  } no-repeat center;
      background-size: 16px;
      background-color: ${foregroundColour};
      border-color: ${foregroundColour};
    }
  `}
`;

export const PlayerWrapper = styled.div`
  background-color: #222;
  padding: 5rem 0;
`;
